import * as React from "react";
import Login from "./components/screens/Login";
import { Routes, Route, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Protected } from "./components/screens/Protected";
import { ScreenUsers } from "./components/screens/protected/Users";
import { ScreenProductions } from "./components/screens/protected/production/Productions";
import UserDetails from "./features/users/user-details";
import { ScreenStudies } from "./components/screens/protected/Studies";
import { ScreenMaterials } from "./components/screens/protected/Materials";
import { ScreenOrders } from "./components/screens/protected/Orders";
import { ScreenCenters } from "./components/screens/protected/center/Centers";
import { ScreenSponsors } from "./components/screens/protected/Sponsors";
import { grey } from "@mui/material/colors";
import { useSelector } from "react-redux";
import { ScreenCreateOrderForm } from "./components/screens/protected/CreateOrderForm";
import { ScreenConfirmOrderForm } from "./components/screens/protected/ConfirmOrderForm";
import { ScreenCenterInfo } from "./components/screens/protected/center/CenterInfo";
import { ScreenOrderInfo } from "./components/screens/protected/OrderInfo";
import { ScreenCreateShippingForm } from "./components/screens/protected/CreateShippingForm";
import { ScreenConfirmShippingForm } from "./components/screens/protected/ConfirmShippingForm";
import { ScreenShippingInfo } from "./components/screens/protected/ShipmentInfo";
import { ScreenAorForm } from "./components/screens/protected/AorForm";
import { ScreenAor } from "./components/screens/protected/Aor";
import { ScreenStudyInfo } from "./components/screens/protected/StudyInfo";
import { ScreenProfile } from "./components/screens/protected/Profile";
import LegalNotice from "./components/screens/LegalNotice";
import { ScreenAudit } from "./components/screens/protected/Audit";
import { ScreenSampleShipments } from "./components/screens/protected/sample/sample-shipment/SampleShipments";
import { ScreenSamples } from "./components/screens/protected/sample/sample/Samples";
import { ScreenSampleForm } from "./components/screens/protected/sample/sample/SampleForm";
import { ScreenSampleRequestForm } from "./components/screens/protected/sample/sample-shipment/SampleRequestForm";
import ScreenSampleShipmentReceptionForm from "./components/screens/protected/sample/sample-shipment/SampleShipmentReceptionForm";
import ScreenSampleShipmentBiobank from "./components/screens/protected/sample/biobanking/SampleShipmentBiobank";
import { ScreenAnalyses } from "./components/screens/protected/sample/analysis/Analyses";
import { ScreenAnalysisForm } from "./components/screens/protected/sample/analysis/AnalysisForm";
import { ScreenSampleSetup } from "./components/screens/protected/sample/SampleSetup";
import { ScreenSampleShipmentDetail } from "./components/screens/protected/sample/sample-shipment/SampleShipmentDetail";
import { ScreenExports } from "./components/screens/protected/Exports";
import { ScreenSampleRequestFormConfirm } from "./components/screens/protected/sample/sample-shipment/SampleRequestFormConfirm";
import { ScreenAnalytic } from "./features/analytic/analytic-page";
import Toast from "./shared/ui/toast/Toast";

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: "#1e3179",
    },
    secondary: {
      main: "#a64d79",
    },
  },
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: grey[300],
          },
        },
      },
    },
  },
});

export default function App() {
  const accessToken = useSelector((state) => state.root.accessToken);
  const user = useSelector((state) => state.user.user);
  const isAdmin = user?.role === "admin";
  const centerUser = user?.organization === "Center";
  const centerManagerUser = centerUser && user?.role === "manager";
  const analyticUser = !centerUser || centerManagerUser;
  const loading = Boolean(accessToken && !user);
  const defaultProtectedRoute = centerManagerUser
    ? "/studies"
    : centerUser
      ? "/productions"
      : "/studies";

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div data-testid="app">
      <ThemeProvider theme={defaultTheme}>
        <Toast />
        <Routes>
          <Route path="/legal" element={<LegalNotice />} />
          {accessToken ? (
            <Route path="/" element={<Protected />}>
              <Route
                path="/"
                element={<Navigate to={defaultProtectedRoute} />}
              />
              <Route
                path="users"
                element={
                  isAdmin ? (
                    <ScreenUsers />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />

              {/* user detail route */}
              <Route
                path="users/:id"
                element={
                  isAdmin ? (
                    <UserDetails />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />
              {/* ------------------------------------------------- */}
              <Route
                path="studies"
                element={
                  !centerUser || centerManagerUser ? (
                    <ScreenStudies />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />

              <Route
                path="study-info"
                element={
                  isAdmin ? (
                    <ScreenStudyInfo />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />
              {/* ------------------------------------------------- */}

              <Route path="orders" element={<ScreenOrders />} />
              <Route path="order" element={<ScreenOrderInfo />} />
              <Route path="shipping" element={<ScreenShippingInfo />} />
              <Route
                path="shipping/new"
                element={<ScreenCreateShippingForm />}
              />
              <Route
                path="shipping/confirm"
                element={<ScreenConfirmShippingForm />}
              />
              <Route path="aor" element={<ScreenAor />} />
              <Route path="aor-form" element={<ScreenAorForm />} />
              <Route path="productions" element={<ScreenProductions />} />

              {/* --------------------------------- */}
              <Route path="materials" element={<ScreenMaterials />} />

              <Route path="samples" element={<ScreenSamples />} />
              <Route path="samples/new" element={<ScreenSampleForm />} />
              <Route path="samples/:id" element={<ScreenSampleForm />} />
              <Route
                path="sample-shipments"
                element={<ScreenSampleShipments />}
              />
              <Route
                path="sample-shipments/new"
                element={<ScreenSampleRequestForm />}
              />
              <Route
                path="sample-shipments/confirm"
                element={<ScreenSampleRequestFormConfirm />}
              />
              <Route
                path="sample-shipments/summary"
                element={<ScreenSampleShipmentDetail />}
              />
              <Route
                path="sample-shipments/:reference/reception"
                element={<ScreenSampleShipmentReceptionForm />}
              />
              <Route
                path="sample-shipments/:reference/biobank"
                element={<ScreenSampleShipmentBiobank />}
              />
              <Route
                path="sample-shipments/:reference"
                element={<ScreenSampleShipmentDetail />}
              />
              <Route
                path="analysis"
                element={<Navigate to="/sample/analysis" replace />}
              />
              <Route path="sample/analysis" element={<ScreenAnalyses />} />
              <Route
                path="sample/analysis/new"
                element={<ScreenAnalysisForm />}
              />
              <Route
                path="sample/analysis/:id"
                element={<ScreenAnalysisForm />}
              />
              <Route
                path="sample/setup"
                element={
                  isAdmin ? (
                    <ScreenSampleSetup />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />
              <Route
                path="analytic"
                element={
                  analyticUser ? (
                    <ScreenAnalytic />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />

              {/* --------------------------------- */}
              <Route
                path="centers"
                element={
                  !centerUser || centerManagerUser ? (
                    <ScreenCenters />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />

              <Route
                path="center-info"
                element={
                  isAdmin ? (
                    <ScreenCenterInfo />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />
              <Route
                path="sponsors"
                element={
                  isAdmin ? (
                    <ScreenSponsors />
                  ) : (
                    <Navigate to={defaultProtectedRoute} />
                  )
                }
              />
              <Route path="orders/new" element={<ScreenCreateOrderForm />} />
              <Route
                path="orders/confirm"
                element={<ScreenConfirmOrderForm />}
              />
              <Route path="profile" element={<ScreenProfile />} />
              <Route path="exports" element={<ScreenExports />} />
              <Route path="/*" element={<Navigate to="/" />} />
              <Route
                path="/audit"
                element={isAdmin ? <ScreenAudit /> : <Navigate to="/" />}
              />
            </Route>
          ) : (
            <>
              <Route path="/" element={<Login />} />
              <Route path="/*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
      </ThemeProvider>
    </div>
  );
}
